import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { MaintenanceRequestService } from '../services/asset-maintenance-request/asset-maintenance-request.service';
import { ToasterService, AuthService } from '@ui-nuvertos/auth';
import { lastValueFrom, Subscription, filter, take } from 'rxjs';
import { SubsidiaryService } from '../services/subsidiary/subsidiary.service';
import { ListStateService } from '../services/list-state/list-state.service';
import moment from 'moment';
import {
  DynamicListingComponent,
  FilterConfig,
  ColumnConfig,
  ActionConfig,
  ActionEvent,
  FilterEvent,
} from '../dynamic-listing/dynamic-listing';
import { SVG_ICONS } from '../utils/svg-icons.utils';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { ExportDialogComponent } from '../export-dialog/export-dialog.component';
import { ExportApiService, CreateExportJobRequest } from '../services/export/export-api.service';
import { openInNewTab } from '../utils/url.utils';

@Component({
  selector: 'lib-my-maintenance-requests',
  standalone: true,
  imports: [CommonModule, DynamicListingComponent, DynamicDialogModule],
  providers: [DialogService],
  templateUrl: './my-maintenance-requests.html',
  styleUrls: ['./my-maintenance-requests.css'],
})
export class MyMaintenanceRequests implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly listStateService = inject(ListStateService);
  private pageSubscription?: Subscription;

  exportFilename = `maintenance_requests_${new Date().toISOString().slice(0, 10)}`;

  tableData: any[] = [];
  totalCount = 0;
  isLoading = false;
  pageIndex = 1;
  rowsPerPage = 10;
  totalPages = 0;
  columnPreferenceId?: string;
  userId: string | null = null;
  
  requestDate: Date | null = null;
  subsidiaryId: number | null = null;
  locationId: number | null = null;
  requestId: string = '';
  assetIdSearch: any = null;

  filterConfigs: FilterConfig[] = [];
  columnConfigs: ColumnConfig[] = [];

  subsidiaryOptions: any[] = [];
  subsidiariesData: any[] = [];
  locationOptions: any[] = [];

  actionConfigs: ActionConfig[] = [
    { key: 'view', label: 'View', tooltip: 'View', severity: 'secondary', svgIcon: SVG_ICONS.VIEW },
    { key: 'edit', label: 'Edit', tooltip: 'Take Action', severity: 'secondary', svgIcon: SVG_ICONS.ACTION ,
      disabled: (row) => row.raw?.status === 'completed' || row.raw?.assign_to_user_id !== this.userId,
      disabledTooltip: (row) => row.raw?.status === 'completed' ? 'No action available for completed requests' : 'Action is only available for requests assigned to you'
    },
  ];

  constructor(
    private maintenanceRequestService: MaintenanceRequestService,
    private subsidiaryService: SubsidiaryService,
    private router: Router,
    private toasterService: ToasterService,
    private dialogService: DialogService,
    private exportApiService: ExportApiService,
    private authService: AuthService
  ) {}

  ngOnDestroy() {
    this.pageSubscription?.unsubscribe();
  }

  async ngOnInit() {
    try {
      const currentUser = await lastValueFrom(
        this.authService.currentUser$.pipe(
          filter(user => !!user),
          take(1)
        )
      );
      this.userId = currentUser?.id || null;

      await this.loadFiltersData();
      this.initializeFilterConfigs();

      this.pageSubscription = this.listStateService.getPageFromRoute(this.route).subscribe(page => {
        this.loadMaintenanceRequests(page);
      });
    } catch (error) {
      console.error('Error in ngOnInit:', error);
      this.isLoading = false;
    }
  }

  async loadFiltersData() {
    try {
      const subResponse = await lastValueFrom(this.subsidiaryService.getSubsidiaries({ limit: '1000', is_active: 'true' }));
      if (subResponse?.data?.subsidiaries) {
        this.subsidiariesData = subResponse.data.subsidiaries;
        this.subsidiaryOptions = this.subsidiariesData.map((s: any) => ({
          label: s.name,
          value: s.id
        }));
      }
    } catch (error) {
      console.error('Error loading filter data:', error);
    }
    this.updateLocationOptions(this.subsidiaryId);
  }

  updateLocationOptions(subsidiaryId: any) {
    if (!subsidiaryId) {
      this.locationOptions = [];
    } else {
      const subsidiary = this.subsidiariesData.find((s: any) => String(s.id) === String(subsidiaryId));
      if (subsidiary?.tax_information && Array.isArray(subsidiary.tax_information)) {
        this.locationOptions = subsidiary.tax_information
          .filter((t: any) => !t.is_deleted)
          .map((t: any) => ({
            label: t.address || `Location ${t.id}`,
            value: t.id
          }));
      } else {
        this.locationOptions = [];
      }
    }
    const locationFilterIndex = this.filterConfigs.findIndex(f => f.key === 'locationId');
    if (locationFilterIndex !== -1) {
      this.filterConfigs[locationFilterIndex].options = this.locationOptions;
      this.filterConfigs = [...this.filterConfigs];
    }
  }

  async handleAssetSearch(query: string, filter: FilterConfig, searchType: 'id' | 'name') {
    try {
      const res = await lastValueFrom(this.maintenanceRequestService.getMaintenanceRequests({ limit: 20, search: query }));
      if (res?.data?.data) {
        const results = res.data.data;
        const suggestions = results.map((item: any) => ({
          label: searchType === 'id' ? (item.asset_code || item.maintenance_request_id) : (item.asset_name || item.asset_code),
          value: searchType === 'id' ? item.asset_code : item.asset_name
        }));
        
        const uniqueSuggestions = Array.from(new Map(suggestions.map((item: any) => [item.label, item])).values());
        
        const filterIndex = this.filterConfigs.findIndex(f => f.key === filter.key);
        if (filterIndex !== -1) {
          this.filterConfigs[filterIndex].suggestions = uniqueSuggestions;
        }
      }
    } catch (error) {
      console.error('Error searching assets:', error);
    }
  }

  initializeFilterConfigs() {
    this.filterConfigs = [
      {
        key: 'requestId',
        type: 'text',
        placeholder: 'Request ID',
        value: this.requestId,
        showInAccordion: true,
        showInDialog: false,
      },
      {
        key: 'assetIdSearch',
        type: 'autocomplete',
        placeholder: 'Asset ID',
        value: this.assetIdSearch,
        showInAccordion: true,
        showInDialog: false,
        forceSelection: false,
        completeMethod: async (event: { query: string; filter: FilterConfig }) => {
          await this.handleAssetSearch(event.query, event.filter, 'id');
        }
      },
      {
        key: 'subsidiaryId',
        type: 'select',
        placeholder: 'Subsidiary',
        value: this.subsidiaryId,
        options: this.subsidiaryOptions,
        showInAccordion: true,
        showInDialog: false,
      },
      {
        key: 'locationId',
        type: 'select',
        placeholder: 'Location',
        value: this.locationId,
        options: this.locationOptions,
        showInAccordion: true,
        showInDialog: false,
      },
      {
        key: 'requestDate',
        type: 'date',
        placeholder: 'Request Date',
        value: this.requestDate,
        showInAccordion: true,
        showInDialog: false,
      },
    ];
  }

  async loadMaintenanceRequests(page: number = 1, skipLoading = false) {
    try {
      if (!skipLoading) {
        this.isLoading = true;
      }
      this.pageIndex = page;

      const apiFilters: any = {
        limit: this.rowsPerPage,
        offset: (page - 1) * this.rowsPerPage,
        get_references: 'true',
      };

      if (this.requestId?.trim()) apiFilters.request_id = this.requestId.trim();
      if (this.assetIdSearch) apiFilters.asset_id = this.assetIdSearch; // Handled by autocomplete value
      if (this.subsidiaryId) apiFilters.subsidiary_id = this.subsidiaryId;
      if (this.locationId) apiFilters.location_id = this.locationId;
      if (this.requestDate) apiFilters.request_date = moment(this.requestDate).format('YYYY-MM-DD');

      if (this.userId) {
        apiFilters.created_by = this.userId;
        apiFilters.assign_to_user_id = this.userId;
      }

      const response = await lastValueFrom(this.maintenanceRequestService.getMaintenanceRequests(apiFilters));

      // Define all standard columns matching backend model fields.
      // Excludes: URL/copy fields (warranty_copy, insurance_copy, amc_copy, photos),
      // sub-table arrays (part_items, fulfillment, work_detail), and internal IDs.
      const standardColumns: ColumnConfig[] = [
        // Core identifiers
        { key: 'maintenance_request_id', header: 'Request ID', type: 'text', is_selected: true },
        { key: 'asset_code', header: 'Asset Code', type: 'text', is_selected: true },
        { key: 'asset_name', header: 'Asset Name', type: 'text', is_selected: true },
        { key: 'barcode', header: 'Barcode', type: 'text', is_selected: false },
        { key: 'serial_number', header: 'Serial Number', type: 'text', is_selected: false },
        { key: 'asset_group_name', header: 'Asset Group', type: 'text', is_selected: false },
        { key: 'asset_sub_group_name', header: 'Asset Sub Group', type: 'text', is_selected: false },
        { key: 'asset_type', header: 'Asset Type', type: 'text', is_selected: false },
        { key: 'model_no', header: 'Model No', type: 'text', is_selected: false },
        // Request details
        { key: 'issue_type', header: 'Issue Type', type: 'text', is_selected: true },
        { key: 'priority', header: 'Priority', type: 'status', is_selected: true },
        { key: 'status', header: 'Status', type: 'status', is_selected: true },
        { key: 'approval_status', header: 'Approval Status', type: 'status', is_selected: false },
        { key: 'request_date', header: 'Request Date', type: 'date', is_selected: true },
        { key: 'created_by_name', header: 'Created By', type: 'text', is_selected: true },
        { key: 'type_of_request', header: 'Type of Request', type: 'text', is_selected: false },
        { key: 'description', header: 'Description', type: 'text', is_selected: false },
        { key: 'is_critical', header: 'Is Critical', type: 'text', is_selected: false },
        { key: 'parent_maintenance_request_no', header: 'Parent Request No', type: 'text', is_selected: false },
        { key: 'previous_asset_status', header: 'Previous Asset Status', type: 'text', is_selected: false },
        // Warranty / AMC / Insurance
        { key: 'warranty_status', header: 'Warranty Status', type: 'text', is_selected: false },
        { key: 'warranty_expiry', header: 'Warranty Expiry', type: 'date', is_selected: false },
        { key: 'amc_started', header: 'AMC Started', type: 'text', is_selected: false },
        { key: 'amc_expiry', header: 'AMC Expiry', type: 'date', is_selected: false },
        { key: 'next_maintenance', header: 'Next Maintenance', type: 'text', is_selected: false },
        { key: 'insurance_policy_no', header: 'Insurance Policy No', type: 'text', is_selected: false },
        { key: 'insurance_expiry', header: 'Insurance Expiry', type: 'date', is_selected: false },
        // Location
        { key: 'subsidiary_name', header: 'Subsidiary', type: 'text', is_selected: false },
        { key: 'location_name', header: 'Location', type: 'text', is_selected: false },
        { key: 'vendor_name', header: 'Vendor Name', type: 'text', is_selected: false },
        // Fulfillment Details
        { key: 'assign_request_to', header: 'Assign Request To', type: 'status', is_selected: false },
        { key: 'assign_to_user_name', header: 'Assign To User', type: 'text', is_selected: false },
        { key: 'department', header: 'Department', type: 'text', is_selected: false },
        { key: 'expected_start_date', header: 'Expected Start Date', type: 'date', is_selected: false },
        { key: 'expected_resolution_date', header: 'Expected Resolution Date', type: 'date', is_selected: false },
        { key: 'fulfillment_vendor_name', header: 'Fulfillment Vendor Name', type: 'text', is_selected: false },
        { key: 'contact_person', header: 'Contact Person', type: 'text', is_selected: false },
        { key: 'contact_number', header: 'Contact Number', type: 'text', is_selected: false },
        { key: 'covered_under', header: 'Covered Under', type: 'text', is_selected: false },
        { key: 'estimated_repair_cost', header: 'Estimated Repair Cost', type: 'currency', is_selected: false },
        { key: 'need_to_send_out', header: 'Need To Send Out', type: 'text', is_selected: false },
        { key: 'send_to_location', header: 'Send To Location', type: 'text', is_selected: false },
        { key: 'dispatch_date', header: 'Dispatch Date', type: 'date', is_selected: false },
        { key: 'expected_return_date', header: 'Expected Return Date', type: 'date', is_selected: false },
        { key: 'transport_mode', header: 'Transport Mode', type: 'text', is_selected: false },
        { key: 'pickup_challan', header: 'Pickup Challan', type: 'text', is_selected: false },
        // Work Details
        { key: 'initial_diagnosis', header: 'Initial Diagnosis', type: 'text', is_selected: false },
        { key: 'is_asset_operational', header: 'Is Asset Operational?', type: 'text', is_selected: false },
        { key: 'is_vendor_likely_required', header: 'Is Vendor Likely Required?', type: 'text', is_selected: false },
        { key: 'request_to_discard', header: 'Request to Discard?', type: 'text', is_selected: false },
        { key: 'return_fixed_date', header: 'Return Fixed Date', type: 'date', is_selected: false },
        { key: 'received_by_user_name', header: 'Received By', type: 'text', is_selected: false },
        { key: 'return_mode', header: 'Return Mode', type: 'text', is_selected: false },
        { key: 'delivery_challan', header: 'Delivery Challan', type: 'text', is_selected: false },
        { key: 'maintenance_status', header: 'Maintenance Status', type: 'status', is_selected: false },
        { key: 'vendor_notes', header: 'Vendor Notes', type: 'text', is_selected: false },
        { key: 'pi', header: 'PI', type: 'text', is_selected: false },
        { key: 'claim_no', header: 'Claim No', type: 'text', is_selected: false },
        { key: 'amount', header: 'Amount', type: 'currency', is_selected: false },
        { key: 'asset_condition', header: 'Asset Condition', type: 'status', is_selected: false },
        { key: 'functional_test_done', header: 'Functional Test Done?', type: 'text', is_selected: false },
        { key: 'parts_replaced', header: 'Parts Replaced?', type: 'text', is_selected: false },
        // Asset detail (from JOINs)
        { key: 'purchase_order_no', header: 'Purchase Order No', type: 'text', is_selected: false },
        { key: 'grn_no', header: 'GRN No', type: 'text', is_selected: false },
        { key: 'purchase_date', header: 'Purchase Date', type: 'date', is_selected: false },
        { key: 'purchase_bill_value', header: 'Purchase Bill Value', type: 'currency', is_selected: false },
        { key: 'financial_year', header: 'Financial Year', type: 'text', is_selected: false },
        { key: 'depreciation_type', header: 'Depreciation Type', type: 'text', is_selected: false },
        { key: 'depreciation_rate', header: 'Depreciation Rate', type: 'text', is_selected: false },
        { key: 'capitalization_date', header: 'Capitalization Date', type: 'date', is_selected: false },
        { key: 'asset_life', header: 'Asset Life', type: 'text', is_selected: false },
        // Audit
        { key: 'created_at', header: 'Created At', type: 'date', is_selected: false },
      ];

      if (response?.data?.column_preference) {
        const columnPreference = response.data.column_preference;
        this.columnPreferenceId = columnPreference.id ? String(columnPreference.id) : undefined;

        if (columnPreference.columns_list && Array.isArray(columnPreference.columns_list)) {
          this.columnConfigs = columnPreference.columns_list.map((col: any) => ({
              key: col.key,
              header: col.header || col.label,
              type: col.type,
              ellipsis: col.ellipsis,
              align: col.align,
              is_selected: col.is_selected !== false,
              customField: col.customField || col.is_custom_field || false,
          }));
        }
      }

      // Fallback if still empty
      if (this.columnConfigs.length === 0) {
        this.columnConfigs = [...standardColumns];
      }

      // Extract data
      if (response?.data?.data && response.status === 'success') {
        this.tableData = response.data.data.map((request: any) => ({
          ...request,
          ...(request.custom_fields || {}),
          id: request.id,
          maintenance_request_id: request.maintenance_request_id,
          asset_name: request.asset_name || '-',
          asset_code: request.asset_code || '-',
          issue_type: request.issue_type || '-',
          priority: this.formatStatus(request.priority) || '-',
          status: this.formatStatus(request.status) || '-',
          request_date: request.request_date,
          created_by_name: request.created_by_name || '-',
          assign_request_to: request.assign_request_to ? this.formatStatus(request.assign_request_to) : '-',
          maintenance_status: request.maintenance_status ? this.formatStatus(request.maintenance_status) : '-',
          asset_condition: request.asset_condition ? this.formatStatus(request.asset_condition) : '-',
          parts_replaced: request.parts_replaced ? 'Yes' : 'No',
          is_critical: request.is_critical ? 'Yes' : 'No',
          need_to_send_out: request.need_to_send_out ? 'Yes' : 'No',
          is_asset_operational: request.is_asset_operational ? 'Yes' : 'No',
          is_vendor_likely_required: request.is_vendor_likely_required ? 'Yes' : 'No',
          request_to_discard: request.request_to_discard ? 'Yes' : 'No',
          functional_test_done: request.functional_test_done ? 'Yes' : 'No',
          assign_to_user_id: request.assign_to_user_id || null,
          raw: request,
        }));
        this.totalCount = response.data.total || 0;
        this.totalPages = Math.ceil(this.totalCount / this.rowsPerPage) || 1;
      } else {
        this.tableData = [];
        this.totalCount = 0;
        this.totalPages = 0;
      }
    } catch (error) {
      console.error('Error loading maintenance requests:', error);
      this.tableData = [];
      this.totalCount = 0;
      this.totalPages = 0;
    } finally {
      if (!skipLoading) {
        this.isLoading = false;
      }
    }
  }

  formatStatus(status: string): string {
    if (!status) return '-';
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  onFilterEvent(event: FilterEvent) {
    const extractValue = (val: any) => (typeof val === 'object' && val !== null) ? val.value : val;
    this.assetIdSearch = extractValue(event.data?.assetIdSearch) || null;
    this.requestId = event.data?.requestId || '';
    this.requestDate = event.data?.requestDate || null;

    const newSubsidiaryId = event.data?.subsidiaryId || null;
    if (newSubsidiaryId !== this.subsidiaryId) {
      this.subsidiaryId = newSubsidiaryId;
      this.locationId = null; // Reset location when subsidiary changes
      this.updateLocationOptions(this.subsidiaryId);
    } else {
      this.locationId = event.data?.locationId || null;
    }

    this.listStateService.clearPage();
    this.loadMaintenanceRequests(1);
  }

  onActionClicked(event: ActionEvent) {
    switch (event.action) {
      case 'view':
        this.router.navigate(['/assets/asset-maintenance-requests', event.row.id], {
          queryParams: { mode: 'view' },
        });
        break;
      case 'edit':
        this.router.navigate(['/assets/asset-maintenance-requests', event.row.id], {
          queryParams: { mode: 'edit' },
        });
        break;
      case 'delete':
        this.deleteRequest(event.row.id);
        break;
    }
  }

  onPageChanged(page: number) {
    this.listStateService.setPage(page);
  }

  onColumnPreferenceSaved(event: { id: string; columns: ColumnConfig[] }) {
    this.columnPreferenceId = event.id ? String(event.id) : undefined;
    this.columnConfigs = event.columns;
  }

  exportCsv() {
    this.openExportDialog('csv');
  }

  exportXlsx() {
    this.openExportDialog('excel');
  }

  exportPdf() {
    window.print();
  }

  private openExportDialog(format: 'excel' | 'csv'): void {
    const dialogRef = this.dialogService.open(ExportDialogComponent, {
      header: 'Export Maintenance Requests',
      width: '500px',
      data: {
        module: 'my_maintenance_request',
        columns: this.columnConfigs,
        filters: this.getCurrentFilters(),
        baseFilters: {
          created_by: this.userId,
          assign_to_user_id: this.userId
        },
        defaultFormat: format,
      },
    });

    if (dialogRef) {
      dialogRef.onClose.subscribe((request: CreateExportJobRequest) => {
        if (request) {
          this.startExport(request);
        }
      });
    }
  }

  private getCurrentFilters(): Record<string, any> {
    return {
      request_id: this.requestId?.trim() || undefined,
      asset_id: this.assetIdSearch || undefined,
      subsidiary_id: this.subsidiaryId || undefined,
      location_id: this.locationId || undefined,
      request_date: this.requestDate ? moment(this.requestDate).format('YYYY-MM-DD') : undefined,
    };
  }

  private startExport(request: CreateExportJobRequest): void {
    this.exportApiService.createExportJob(request).subscribe({
      next: () => {
        this.toasterService.success(
          'Export Started',
          'Your export is processing. Click here to view progress →',
          5000
        );
        setTimeout(() => openInNewTab('/exports'), 1500);
      },
      error: (err: any) => {
        const message = err?.error?.message || 'Failed to start export';
        this.toasterService.error('Export Failed', message);
      },
    });
  }

  async deleteRequest(requestId: string) {
    if (confirm('Are you sure you want to delete this maintenance request?')) {
      try {
        await lastValueFrom(this.maintenanceRequestService.deleteMaintenanceRequest(requestId));
        this.toasterService.success('Maintenance request deleted successfully');
        await this.loadMaintenanceRequests(this.pageIndex);
      } catch (error) {
        console.error('Failed to delete maintenance request:', error);
        this.toasterService.error('Failed to delete maintenance request. Please try again.');
      }
    }
  }
}
